import { useState } from "react";
import type { AdminGigIssueListItemResponse, AdminGigIssueScope } from "../../../types/adminTypes";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import { formatDateTime } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminTableShell from "../ui/AdminTableShell";
import AdminIssueDetailModal from "./AdminIssueDetailModal";
import AdminIssuesFilters from "./AdminIssuesFilters";
import AdminIssuesTableRow from "./AdminIssuesTableRow";
import { adminIssuesColumns } from "./adminIssuesColumns";

type AdminIssuesTableProps = {
  issues: AdminGigIssueListItemResponse[];
  isLoading: boolean;
  canWrite: boolean;
  search: string;
  status: string;
  issueScope: "" | AdminGigIssueScope;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onIssueScopeChange: (value: "" | AdminGigIssueScope) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
};

const AdminIssuesTable = ({
  issues,
  isLoading,
  canWrite,
  search,
  status,
  issueScope,
  createdFromDate,
  createdToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onStatusChange,
  onIssueScopeChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onPageChange,
}: AdminIssuesTableProps) => {
  const [selectedIssue, setSelectedIssue] = useState<AdminGigIssueListItemResponse | null>(null);
  const statusTone: Record<AdminGigIssueListItemResponse["status"], "warning" | "success" | "danger"> = {
    OPEN: "warning",
    UNDER_REVIEW: "warning",
    AWAITING_SETTLEMENT_APPROVAL: "warning",
    RESOLVED_RELEASE_PAYOUT: "success",
    RESOLVED_SETTLEMENT: "success",
    RESOLVED_REFUND_ADULT: "danger",
    RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "danger",
    RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "success",
    RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "success",
  };
  const statusLabel: Record<AdminGigIssueListItemResponse["status"], string> = {
    OPEN: "Open",
    UNDER_REVIEW: "Under Review",
    AWAITING_SETTLEMENT_APPROVAL: "Awaiting Settlement Approval",
    RESOLVED_RELEASE_PAYOUT: "Resolved: Payout Released",
    RESOLVED_SETTLEMENT: "Resolved: Settlement Applied",
    RESOLVED_REFUND_ADULT: "Resolved: Refunded",
    RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "Resolved: Refunded Less Fee",
    RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "Resolved: Preparation Payout",
    RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "Resolved: Teen Removed, Funds Held",
  };
  const issueScopeLabel: Record<AdminGigIssueListItemResponse["issueScope"], string> = {
    TEEN_ASSIGNMENT: "Teen assignment",
    WHOLE_GIG: "Whole gig",
  };

  return (
    <>
      <AdminTableShell
        columns={adminIssuesColumns}
        filters={
          <AdminIssuesFilters
            search={search}
            status={status}
            issueScope={issueScope}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onStatusChange={onStatusChange}
            onIssueScopeChange={onIssueScopeChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onPageSizeChange={onPageSizeChange}
          />
        }
        mobileCards={issues.map((issue) => (
          <article key={issue.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-myteengig-text-primary">{issue.gigTitle ?? "Untitled gig"}</p>
                <p className="mt-1 line-clamp-2 text-sm text-myteengig-text-muted">
                  {issue.messagePreview || "No issue details provided."}
                </p>
              </div>
              <AdminPillBadge tone={statusTone[issue.status]}>{statusLabel[issue.status]}</AdminPillBadge>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Reported by</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <AdminPillBadge>{issue.reportedByRole}</AdminPillBadge>
                  <AdminPillBadge>{issueScopeLabel[issue.issueScope]}</AdminPillBadge>
                  <span className="text-sm text-myteengig-text-primary">{issue.reportedByName}</span>
                </div>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Participants</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">Teen: {issue.teenName}</p>
                <p className="mt-1 text-sm text-myteengig-text-muted">Adult: {issue.adultName}</p>
                {issue.coveredSeats ? <p className="mt-1 text-sm text-myteengig-text-muted">Seats: {issue.coveredSeats}</p> : null}
                {issue.gigFundingAssignmentId ? (
                  <p className="mt-1 break-all text-xs text-myteengig-text-muted">Assignment: {issue.gigFundingAssignmentId}</p>
                ) : null}
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDateTime(issue.createdAt)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Resolved</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">
                  {issue.resolvedAt ? formatDateTime(issue.resolvedAt) : "Not resolved"}
                </p>
                {issue.paymentStatus ? <p className="mt-1 text-sm text-myteengig-text-muted">Payment: {issue.paymentStatus}</p> : null}
                {issue.allocatedPosterChargeCents !== null ? (
                  <p className="mt-1 text-sm text-myteengig-text-muted">
                    Allocation: {formatUsdFromCents(issue.allocatedPosterChargeCents)}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedIssue(issue)}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={issues.map((issue) => (
          <AdminIssuesTableRow key={issue.id} issue={issue} onView={setSelectedIssue} />
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && issues.length === 0}
        emptyMessage="No gig issues found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminIssueDetailModal
        issue={selectedIssue}
        isOpen={selectedIssue !== null}
        canWrite={canWrite}
        onClose={() => setSelectedIssue(null)}
      />
    </>
  );
};

export default AdminIssuesTable;
